import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from './services/auth.service';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './components/header/header.component';
import { NRLoaderComponent } from './shared/components/nr-loader/nr-loader.component';
import { FooterComponent } from '@components/footer/footer.component';
import { NrBannerComponent } from '@shared/components/nr-banner/nr-banner.component';
import { filter, fromEvent, merge, Subject, takeUntil, throttleTime } from 'rxjs';
import { UserAuthService } from '@services/user-auth.service';
import { CatCounterService } from '@services/cat-counter.service';
import { IUser } from '@shared/interfaces/interface';
import { CatService } from '@modules/cat/services/cat.service';
import moment from 'moment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, NRLoaderComponent, FooterComponent, NrBannerComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'rows';
  isIframe = false;
  private readonly router = inject(Router);
  private readonly userAuthService = inject(UserAuthService);
  private readonly catCounterService = inject(CatCounterService);
  private readonly destroy$ = new Subject<void>();
  private currentUser: IUser | null = null;
  private readonly catService = inject(CatService);

  constructor(
    private readonly authSvc: AuthService,
    public modalConfig: NgbModalConfig,
  ) {
    this.modalConfig.centered = true;
  }

  ngOnInit(): void {
    // this.normalizeInitialHashRoute();
    const browserGlobal = globalThis as unknown as Window;
    this.isIframe = browserGlobal !== browserGlobal.parent && !browserGlobal.opener; // Remove this line to use Angular Universal
    this.authSvc.broadCast();

    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        window.scrollTo(0, 0); // Scrolls to top on every route change
        // Clear the rewriting flag once the final navigation settles
        // (window as any).__isHashRewriting = false;
      });

    this.registerServiceWorker();
    this.initializeCatCounterRefresh();
    this.initializeAppResumeListeners();
  }

  private initializeCatCounterRefresh(): void {
    this.userAuthService
      .getUser()
      .pipe(
        // Wait for a real user profile (non-zero routeId and not firstLogin)
        filter((u) => !!u && !!u.routeId && u.routeId !== 0 && u.firstLogin === false),
        takeUntil(this.destroy$),
      )
      .subscribe((user) => {
        this.currentUser = user;
        this.catCounterService.refreshCatCount([user.routeId], this.router.url.includes('/cat'));
      });
  }

  private initializeAppResumeListeners(): void {
    const browserGlobal = globalThis as unknown as Window;
    const resumeEvents = [
      fromEvent(browserGlobal, 'focus'),
      fromEvent(document, 'visibilitychange'),
      fromEvent(browserGlobal, 'online'),
      // Note: Removed 'load' event as it triggers on initial page load,
      // causing duplicate counter API calls. Counter is already refreshed via initializeCatCounterRefresh.
    ];
    // Use merge to treat all resume triggers as ONE stream
    merge(...resumeEvents)
      .pipe(
        takeUntil(this.destroy$),
        // 1. Only act if the tab is actually visible
        filter(() => document.visibilityState === 'visible'),
        // 2. Only allow one execution every 5 seconds.
        // This stops 'focus' and 'visibilitychange' from double-firing.
        throttleTime(5000, undefined, { leading: true, trailing: true }),
      )
      .subscribe(() => {
        console.log('[AppComponent] Unified Resume Trigger', moment.tz('Europe/London'));
        // Always recover state and refresh counter on all pages
        this.catService.recoverState();
        this.refreshHeaderCatCount();

        // Only fetch CAT-specific issue data if on CAT route
        const isCatRoute = this.router.url.includes('/cat');
        if (isCatRoute) {
          this.catService.fetchLatestIssueTime(true).subscribe({
            next: () => console.log('[App] Latest issue time refreshed on resume'),
            error: (err) => console.warn('[App] Failed to refresh issue time on resume:', err),
          });
        }
      });
  }

  private refreshHeaderCatCount(): void {
    if (
      !this.currentUser?.routeId ||
      this.currentUser.routeId === 0 ||
      this.currentUser.firstLogin === true
    ) {
      return;
    }

    this.catCounterService.refreshCatCount(
      [this.currentUser.routeId],
      this.router.url.includes('/cat'),
    );
  }

  private registerServiceWorker(): void {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker
      .register('/sw-sync.js')
      .then(() => {
        console.log('[AppComponent] Service worker registered.');
      })
      .catch((err) => {
        console.warn('[AppComponent] Service worker registration failed:', err);
      });

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'WAKE_UP_CHECK' || event.data?.type === 'CHECK_CONNECTION_STATE') {
        console.log('[AppComponent] Service worker wake-up message received:', event.data.type);
        // Always fetch fresh data when service worker signals wake-up
        this.catService.recoverState();
        this.catService.fetchLatestIssueTime(true).subscribe({
          next: () => console.log('[AppComponent] Sync completed via Service Worker'),
          error: (err) => console.error('[AppComponent] Sync failed via Service Worker', err),
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.authSvc.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**  This method checks if the current URL hash contains a valid navigation path.
    It ignores any hash that contains OAuth/OIDC tokens or codes to prevent interference with authentication flows.
    If a valid path is found, it normalizes the URL by removing the hash and navigating to the corresponding route.
  */
  private getHashNavigationPath(): string | null {
    const hash = window.location.hash || '';
    if (!hash.startsWith('#/')) {
      return null;
    }

    // 2. Generic check: If the hash contains OAuth/OIDC tokens or codes, ignore it
    const hasAuthParams = /(id_token|access_token|code|state)=/.test(hash);
    if (hasAuthParams) {
      return null;
    }

    return hash.substring(1);
  }

  /**  This method normalizes the initial hash route by removing the hash and navigating to the corresponding route.
   */
  private normalizeInitialHashRoute(): void {
    const hashPath = this.getHashNavigationPath();
    if (!hashPath) {
      return;
    }

    const normalizedPath = hashPath.startsWith('/') ? hashPath : `/${hashPath}`;
    if (window.location.pathname !== normalizedPath || window.location.hash) {
      // 1. Set the global flag BEFORE altering history or executing route transitions
      (window as any).__isHashRewriting = true;

      window.history.replaceState(null, '', normalizedPath + window.location.search);

      // 2. Instead of navigateByUrl (which re-triggers loaders and guards),
      // tell the router to quietly update its internal state tree for the current cycle
      this.router.navigate([normalizedPath], {
        replaceUrl: true,
        skipLocationChange: false,
      });
    }
  }
}
